import { Button } from "@volca/ui/components/ui/button";
import {
  SimpleSidebar,
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@volca/ui/components/ui/simple-sidebar";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { WORKBOOK_FRAME } from "@/profitability/constants";
import {
  InteractiveReport,
  RowDetail,
  RowDetailProvider,
  useRowDetail,
} from "@/profitability/interactive-report";
import { RunContent } from "@/profitability/job-costing-run/run-content";
import { useReportRun } from "@/profitability/query";
import { ReportWorkbook } from "@/profitability/report-workbook";
import type { EnrichedProfitabilityRun } from "@/profitability/types";
import { reportError, runDates } from "@/profitability/utils";

const Preview = ({
  businessId,
  run,
}: {
  businessId: string;
  run: EnrichedProfitabilityRun;
}) => {
  const rowDetail = useRowDetail()!;
  const openRow = rowDetail.open;
  return (
    <SimpleSidebarLayout
      open={!!openRow}
      onOpenChange={(open) => {
        if (!open) rowDetail.closeRow();
      }}
    >
      <SimpleSidebarContent>
        <RunContent businessId={businessId} run={run}>
          {run.interactive_available ? (
            <InteractiveReport
              businessId={businessId}
              runId={run.id}
              dates={runDates(run)}
              run={run}
              sheet={run.workbook_available}
              paneInUrl={false}
            />
          ) : (
            <Stack
              gap={0}
              align="stretch"
              className={cn("w-full", {
                [WORKBOOK_FRAME]: run.display.workbookReady,
              })}
            >
              <ReportWorkbook businessId={businessId} runId={run.id} />
            </Stack>
          )}
        </RunContent>
      </SimpleSidebarContent>
      <SimpleSidebar>
        {openRow && (
          <RowDetail open={openRow} onClose={() => rowDetail.closeRow()} />
        )}
      </SimpleSidebar>
    </SimpleSidebarLayout>
  );
};

/** One run of the template, drawn as the run page draws it, for review beside the conversation. */
export const TemplateRunPreview = ({
  businessId,
  runId,
}: {
  businessId: string;
  runId: string;
}) => {
  const query = useReportRun(businessId, runId);
  if (query.isPending)
    return <Skeleton className="h-96 w-full" aria-label="Loading the result" />;
  if (query.error && !query.data)
    return (
      <Stack gap={3} align="start">
        <Text tone="destructive" role="alert">
          The result could not load. {reportError(query.error)}
        </Text>
        <Button variant="secondary" onClick={() => void query.refetch()}>
          Try again
        </Button>
      </Stack>
    );
  return (
    <RowDetailProvider key={query.data.id}>
      <Preview businessId={businessId} run={query.data} />
    </RowDetailProvider>
  );
};
